/**
 * A command's dry run that is still waiting for a live route's price (live supplier pricing spec §6.3): the shell asks
 * again after the longest `retry_after_seconds` any route gave, up to the same number of asks as the price screens, and
 * offers the best route priced so far in place of waiting. The dry run's failed check says the same for Claude.
 */
import { useEffect, useRef } from "react";
import type { PendingPricesBlock } from "@shared-core/domain/object-ui/contract";
import {
  MAX_PRICE_ASKS,
  pendingRoutesOfBlock,
  secondsUntilAskingAgain,
} from "@shared-core/domain/price-still-coming";
import { PendingRouteChoice } from "../PendingRouteChoice";

const FALLBACK_SECONDS = 3;

export function PendingPrices({
  block,
  disabled,
  onAskAgain,
  onUseBest,
}: {
  block: PendingPricesBlock;
  disabled: boolean;
  onAskAgain: () => void;
  onUseBest: (use: Record<string, string>) => void;
}) {
  const asks = useRef(1);
  const askAgain = useRef(onAskAgain);
  useEffect(() => {
    askAgain.current = onAskAgain;
  });
  // One timer per answer: a new answer (a new block) starts the next wait, and asking again while the answer is out
  // never restarts it.
  useEffect(() => {
    if (disabled || asks.current >= MAX_PRICE_ASKS) return;
    const seconds = secondsUntilAskingAgain(
      pendingRoutesOfBlock(block),
      FALLBACK_SECONDS,
    );
    const timer = setTimeout(() => {
      asks.current += 1;
      askAgain.current();
    }, seconds * 1000);
    return () => clearTimeout(timer);
  }, [block, disabled]);

  const best = block.best_priced;
  return (
    <PendingRouteChoice
      pending={pendingRoutesOfBlock(block)}
      best={
        best
          ? { routeLabel: best.route_label, pricePence: best.price_pence }
          : null
      }
      onUseBest={() => {
        if (best) onUseBest(best.use);
      }}
      onWait={() => {
        asks.current = 1;
        askAgain.current();
      }}
    />
  );
}
